import { useEffect, useRef } from "react";

/**
 * Automatically re-calls `fn` when:
 *  1. The page becomes visible again (user switches back to the app/tab)
 *  2. Every `intervalMs` milliseconds while the page is visible (default 30s)
 *
 * `fn` should be a stable reference (e.g. wrapped in useCallback).
 */
export default function useAutoRefresh(fn, intervalMs = 30000) {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    const call = () => {
      if (document.visibilityState === "visible") fnRef.current();
    };

    // Refresh whenever the user returns to this tab / brings the PWA to foreground
    document.addEventListener("visibilitychange", call);

    // Periodic background poll (only fires when tab is visible)
    const id = setInterval(call, intervalMs);

    return () => {
      document.removeEventListener("visibilitychange", call);
      clearInterval(id);
    };
  }, [intervalMs]);
}
